import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Shell from '../../components/Shell/Shell';
import Select from '../../components/Select/Select';
import Button from '../../components/Button/Button';
import { ReactComponent as IconAutoFixHigh } from '../../public/images/svg/icon_auto_fix_high.svg';
import { ReactComponent as IconCopyContent } from '../../public/images/svg/icon_copy_content.svg';
import { StringToJSX } from '../../utils/HtmlParser';
import GroupButton from '../../components/GroupButton/GroupButton';
import SimilarityChecker from '../../components/SimilarityChecker/SimilarityChecker';
import SimilarityCheckerModal from '../../components/SimilarityChecker/SimilarityCheckerModal';
import {
  regenerateContent,
  retrieveGeneratedContent,
  updateSummaryContentRequestId,
  updateLastUsageDate
} from '../../service/ContentGeneration';
import Loading from '../../components/Loading/Loading';
import { sanitize } from '../../utils/HtmlSanitizer';
import FeedbackPanel from '../../components/FeedbackPanel/FeedbackPanel';
import { getPromptHistory } from '../../service/History.service';
import Alert from '../../components/Alert/Alert';
import { extractError, mapError } from '../../utils/alerts/error';
import { extractWarning } from '../../utils/alerts/warning';
import { handleError } from '../../service/base.service';
import { redirectUnauthorized } from '../../utils/errors';
import { wordCounter } from '../../utils/wordCounter';
import { getCurrentDate } from '../../utils/date';
import { getFormSchema } from '../../schemas/forms';
import { CONTENT_TYPE } from '../../enums';
import Copyscape from '../../components/Copyscape/Copyscape';
import CopyscapeDrawer from '../../components/Copyscape/CopyscapeDrawer';
import MetadataPanel from '../../components/MetadataAndSummary/MetadataPanel/MetadataPanel';
import ActionDrawer from '../../components/ActionDrawer/ActionDrawer';
import SummaryPanel from '../../components/MetadataAndSummary/SummaryPanel/SummaryPanel';
import MetaAndSummaryButton from '../../components/MetadataAndSummary/MetaAndSummaryButton';
import { COPY_ANIMATION_DURATION } from '../../constants';
import BlogPartial from './partials/blog';
import InventoryPartial from './partials/inventory';
import MslpPartial from './partials/mslp';
import OtherPartial from './partials/other';

const { value: CONTENT_TYPE_OTHER } = CONTENT_TYPE.OTHER;
const GROUP_BUTTON = [{ label: 'text', active: true }, { label: 'html' }];

const loadSchema = (contentType) => getFormSchema(contentType);

const loadSimilarityChecker = (contentType, similarityReport = {}) =>
  contentType !== CONTENT_TYPE_OTHER
    ? Object.keys(similarityReport || {})
        .map((item, index) => {
          const title = ['example one', 'example two'];
          return { ...similarityReport[item], title: title[index] };
        })
        .filter((item) => !item.error)
    : [];

const retrievePromptData = async (state) => {
  return retrieveGeneratedContent(state);
};

const GeneratedPrompt = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id, requestTryNumber } = useParams();
  const [promptOutput, setPromptOutput] = useState(null);
  const [promptInput, setPromptInput] = useState(state?.input);
  const [promptSimilarity, setPromptSimilarity] = useState(null);
  const [promptCopyscape, setPromptCopyscape] = useState(null);
  const [feedback, setFeedback] = useState(state?.feedback);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('text');
  const [showSimilarityChecker, setSimilarityChecker] = useState(false);
  const [showCopyscapeModal, setCopyscapeModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [summaryContentRequestId, setSummaryContentRequestId] = useState(null);
  const isInitialCall = useRef(true);
  const [showMetaAndSummaryDrawer, setShowMetaAndSummaryDrawer] = useState(false);
  const [hasCopiedContent, setCopiedContent] = useState(false);

  const hasError = error !== null;
  const hasWarning = warning !== null;

  const formik = useFormik({
    initialValues: promptInput,
    enableReinitialize: true,
    validationSchema: loadSchema(promptInput?.contentType),
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: async (form) => {
      isInitialCall.current = true;
      const { wordCount } = form;
      const { lastRequestTryNumber, contentRequestId } = promptInput;

      try {
        const response = await regenerateContent({
          ...form,
          lastRequestTryNumber,
          contentRequestId,
          wordCount: parseInt(wordCount)
        });

        navigate(`/prompt/${response.contentRequestId}/${response.lastRequestTryNumber}`, {
          state: { input: { ...form, ...response, wordCount: parseInt(wordCount) } },
          replace: true
        });
      } catch (e) {
        handleError(e, {
          UnauthorizedError: () => redirectUnauthorized(navigate),
          default: () => setError(mapError(e.name, e.message))
        });
      }
    }
  });

  const setResponseData = useCallback((response = {}) => {
    if (response?.output) {
      setPromptOutput(sanitize(response?.output).replace(/\n\n/g, '\n'));
    }
    setActiveView('text');
    setPromptInput(response?.input);
    setPromptSimilarity(response?.similarityReport);
    setPromptCopyscape(response?.copyscape);
    setFeedback(response?.feedback);
    setError(extractError(response));
    setWarning(extractWarning(response));
    setIsLocked(response.requestTryNumber !== response.lastRequestTryNumber);
    setMetadata(response?.metadata);
    setSummaryContentRequestId(response?.summaryContentRequestId);
  }, []);

  useEffect(() => {
    if (!promptInput && !state) {
      const retrieveHistory = async () => {
        try {
          const response = await getPromptHistory({
            contentRequestId: id,
            lastRequestTryNumber: requestTryNumber
          });

          navigate(`/prompt/${id}/${requestTryNumber}`, {
            state: response,
            replace: true
          });
        } catch (e) {
          handleError(e, {
            UnauthorizedError: () => redirectUnauthorized(navigate),
            default: () => setError(mapError(e.name, e.message))
          });
        }
      };

      retrieveHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (promptInput?.contentRequestId !== state?.input?.contentRequestId) {
      isInitialCall.current = true;
      setPromptInput(state?.input ?? null);
      setFeedback(state?.feedback ?? null);
      setSimilarityChecker(false);
    }
  }, [id, promptInput, requestTryNumber, state]);

  useEffect(() => {
    if (isInitialCall.current) {
      setLoading(true);
      isInitialCall.current = false;
      retrievePromptData({ contentRequestId: id, lastRequestTryNumber: requestTryNumber })
        .then((response) => {
          if (response.status === 'inprogress') {
            return;
          }

          setResponseData(response);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          handleError(e, {
            UnauthorizedError: () => redirectUnauthorized(navigate),
            default: () => setError(mapError(e.name, e.message))
          });
        });
    }
  }, [id, navigate, requestTryNumber, setResponseData, state]);

  const assignSummary = async ({ contentRequestId, requestTryNumber, summaryContentRequestId }) => {
    try {
      await updateSummaryContentRequestId({
        contentRequestId,
        requestTryNumber,
        summaryContentRequestId
      });
    } catch (e) {
      handleError(e, {
        UnauthorizedError: () => redirectUnauthorized(navigate),
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };
  const trackContentUsage = async ({ contentRequestId, requestTryNumber }) => {
    try {
      await updateLastUsageDate({
        contentRequestId,
        requestTryNumber,
        lastUsageDate: getCurrentDate().toISOString()
      });
    } catch (e) {
      handleError(e, {
        UnauthorizedError: () => redirectUnauthorized(navigate),
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };

  const handleViewTypeChange = (value) => {
    setActiveView(value);
  };

  const handleSimilarityChecker = (evt) => {
    evt.stopPropagation();
    setSimilarityChecker(!showSimilarityChecker);
  };

  const handleCopyscapeModal = (evt) => {
    evt.stopPropagation();
    setCopyscapeModal(!showCopyscapeModal);
  };

  const handleMetaAndSummaryDrawer = (evt) => {
    evt.stopPropagation();
    setShowMetaAndSummaryDrawer(!showMetaAndSummaryDrawer);
  };

  const prepareClipboardData = () => {
    const sourceComment = `<!-- source="${window.location.href}" -->`;
    const generatedContent = promptOutput.trim();

    return `${sourceComment}\n${generatedContent}`;
  };

  const handleCopy = () => {
    const clipboardData = prepareClipboardData();

    navigator.clipboard
      .writeText(clipboardData)
      .then(
        () => {
          setCopiedContent(true);
          console.log('Content copied to clipboard');
        },
        () => {
          console.error('Failed to copy');
        }
      )
      .then(() => {
        const { contentRequestId, lastRequestTryNumber: requestTryNumber } = promptInput;
        trackContentUsage({ contentRequestId, requestTryNumber });
      })
      .finally(() => {
        setTimeout(() => {
          setCopiedContent(false);
        }, COPY_ANIMATION_DURATION);
      });
  };

  const renderUserInputForm = (formik) => {
    switch (formik.values?.contentType) {
      case CONTENT_TYPE.BLOG.value:
        return <BlogPartial formik={formik} />;
      case CONTENT_TYPE.INVENTORY.value:
        return <InventoryPartial formik={formik} />;
      case CONTENT_TYPE.MSLP.value:
        return <MslpPartial formik={formik} />;
      case CONTENT_TYPE.OTHER.value:
        return <OtherPartial formik={formik} />;
      default:
        return null;
    }
  };

  const similarityItems = loadSimilarityChecker(formik.values?.contentType, promptSimilarity);

  const hasSimilarity = similarityItems.length > 0;
  const hasCopyscape = promptCopyscape && !promptCopyscape.error;

  return (
    <div key={`prompt-${formik?.values?.contentType}-${id}`}>
      <Shell
        header={
          <div className="flex items-center">
            <FeedbackPanel
              disabled={isLoading || hasError}
              feedback={feedback}
              contentRequestId={promptInput?.contentRequestId}
              requestTryNumber={promptInput?.lastRequestTryNumber}
              onChange={setFeedback}
            />

            <Button
              disabled={isLoading || hasError}
              onClick={handleCopy}
              label={hasCopiedContent ? 'HTML Copied!' : 'Copy HTML'}
              name="copy"
              type="button"
              dataTest="generated-prompt-copy-content"
              className={[
                'transition ease-linear flex justify-center items-center shadow-sm rounded-sm min-w-[200px] py-2 px-8 mr-8 text-white font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50',
                ...(hasCopiedContent ? ['bg-green-800'] : ['bg-purple-200'])
              ].join(' ')}
              icon={<IconCopyContent className="mr-2" />}
            />
          </div>
        }
      >
        <div className="flex gap-8">
          <div className="min-w-[42rem]">
            <div className={'bg-white rounded p-6'}>
              <div className={'grid gap-6 grid-cols-2'}>
                <div className="max-w-[18rem]">
                  <Select
                    id="content-type"
                    label="Content Type"
                    value={formik.values?.contentType}
                    disabled
                    options={CONTENT_TYPE.toArray()}
                    required
                    dataTest="generated-prompt-form-content-type"
                    bottomMarginSizing={1}
                  />
                </div>
              </div>

              {renderUserInputForm(formik)}
            </div>

            <div className="flex justify-center gap-8 py-8">
              <Button
                type="submit"
                name="generate"
                dataTest="generated-prompt-form-generate"
                className="flex justify-center items-center shadow-sm rounded-sm bg-buttonGradient py-4 px-16 text-white font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50"
                onClick={formik.handleSubmit}
                icon={<IconAutoFixHigh className="mr-2" />}
                label={hasError ? 'Retry' : 'Regenerate'}
                disabled={formik.isSubmitting || isLoading || isLocked}
              />
              <Button
                type="button"
                name="reset"
                dataTest="generated-prompt-form-reset"
                className="flex justify-center bg-transparent py-4 text-purple-200 font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50"
                onClick={formik.resetForm}
                label="Reset"
                disabled={formik.isSubmitting || isLoading || isLocked}
              />
            </div>
          </div>

          <div
            data-testid={`generated-prompt-content-${formik.values?.contentType}`}
            className="flex flex-col gap-6 bg-white relative z-10 -my-8 pt-4 pb-6 -mr-9 pr-9 px-8 w-fit shadow-pane`"
          >
            <div className="flex justify-between items-center">
              <div
                data-testid="generated-prompt-content-type"
                className="block text-sm text-grey-300"
              >
                {CONTENT_TYPE.toArray().find(({ value }) => value === formik.values?.contentType)
                  ?.label ?? ''}
              </div>
              <div className="flex items-center justify-between gap-6">
                {isLoading || hasError ? null : (
                  <div
                    data-testid="generated-prompt-content-word-count"
                    className="text-sm text-grey-300"
                  >
                    Word Count: {wordCounter(promptOutput)}
                  </div>
                )}
                <GroupButton
                  dataTest="generated-prompt-type-content"
                  items={GROUP_BUTTON}
                  onClick={handleViewTypeChange}
                  disabled={isLoading || hasError}
                />
              </div>
            </div>

            {isLoading ? (
              <div className="no-twp font-sans flex-grow overflow-auto w-full flex justify-center items-center">
                <Loading />
              </div>
            ) : hasError ? (
              <div className="flex-grow">
                <Alert level="error" label={error.label}>
                  {error.details}
                </Alert>
              </div>
            ) : activeView === GROUP_BUTTON[0].label ? (
              <>
                <div className="overflow-auto flex-grow">
                  {hasWarning && (
                    <Alert level="warning" label={warning.label}>
                      {warning.details}
                    </Alert>
                  )}
                  <div className="no-twp font-sans" data-testid="generated-prompt-content-text">
                    <StringToJSX domString={promptOutput} key="string-parser" />
                  </div>
                </div>

                <div className="">
                  <div className="float-left mr-1 mb-1">
                    <MetaAndSummaryButton onClick={handleMetaAndSummaryDrawer} />

                    <ActionDrawer
                      isOpen={showMetaAndSummaryDrawer}
                      onClick={handleMetaAndSummaryDrawer}
                    >
                      {metadata && <MetadataPanel metadata={metadata} />}

                      <SummaryPanel
                        content={promptOutput ?? ''}
                        contentRequestId={summaryContentRequestId}
                        onChange={async ({
                          contentRequestId: summaryContentRequestId,
                          lastRequestTryNumber: summaryLastRequestTryNumber
                        }) => {
                          if (summaryLastRequestTryNumber > 1) {
                            return;
                          }

                          const { contentRequestId, lastRequestTryNumber: requestTryNumber } =
                            promptInput;

                          await assignSummary({
                            contentRequestId,
                            requestTryNumber,
                            summaryContentRequestId
                          });

                          setSummaryContentRequestId(summaryContentRequestId);
                        }}
                      />
                    </ActionDrawer>
                  </div>

                  <div className="float-left">
                    {hasSimilarity ? (
                      <div className="float-left mr-1 mb-1">
                        <SimilarityChecker
                          items={similarityItems}
                          onClick={handleSimilarityChecker}
                        />
                        <SimilarityCheckerModal
                          isOpen={showSimilarityChecker}
                          items={similarityItems}
                          onClick={handleSimilarityChecker}
                        />
                      </div>
                    ) : null}

                    {hasCopyscape ? (
                      <div className="float-left mr-1 mb-1">
                        <Copyscape data={promptCopyscape} onClick={handleCopyscapeModal} />
                        <CopyscapeDrawer
                          isOpen={showCopyscapeModal}
                          data={promptCopyscape}
                          onClick={handleCopyscapeModal}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-grow overflow-auto">
                {hasWarning && (
                  <Alert level="warning" label={warning.label}>
                    {warning.details}
                  </Alert>
                )}
                <pre
                  data-testid="generated-prompt-content-html"
                  className="whitespace-break-spaces prose-sm"
                >
                  {promptOutput.trim()}
                </pre>
              </div>
            )}
          </div>
        </div>
      </Shell>
    </div>
  );
};

export default GeneratedPrompt;
